import { Button, FloatingPanel } from '@hue/design-system';
import { ChevronDown } from 'lucide-react';
import { useCallback, useId, useRef, useState, type KeyboardEvent } from 'react';
import type { EanPrefix } from './ean.js';

const CHOICES: { prefix: EanPrefix; label: string; detail: string }[] = [
  {
    prefix: 'BRAZIL',
    label: '789 · padrão Brasil',
    detail: 'Não é registrado na GS1. Para marketplace, use o EAN do fabricante ou um licenciado.',
  },
  {
    prefix: 'INTERNAL',
    label: '2 · uso interno',
    detail: 'Para etiquetas da própria empresa: nunca coincide com produto de outra marca.',
  },
];

/// A faixa é escolhida na hora de gerar, ao lado do campo: uma escolha solta no formulário valia
/// para todas as apresentações sem dizer a qual se referia.
export function EanGenerateButton({
  label,
  onGenerate,
}: {
  label: string;
  onGenerate: (prefix: EanPrefix) => void;
}) {
  const [open, setOpen] = useState(false);
  /// O botão do sistema não repassa `ref`; a âncora é o invólucro e o foco volta ao botão dentro dele.
  const anchor = useRef<HTMLSpanElement | null>(null);
  const items = useRef<(HTMLButtonElement | null)[]>([]);
  const menuId = useId();
  const close = useCallback(() => setOpen(false), []);

  /// O painel entra no documento um render depois do menu abrir; o foco vai para a primeira opção
  /// quando ela de fato aparece, uma vez só, para uma nova medição não roubar o foco das setas.
  const focusPending = useRef(false);

  function choose(prefix: EanPrefix) {
    onGenerate(prefix);
    setOpen(false);
    anchor.current?.querySelector('button')?.focus();
  }

  function navigate(event: KeyboardEvent<HTMLDivElement>) {
    const current = items.current.indexOf(document.activeElement as HTMLButtonElement);
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      const step = event.key === 'ArrowDown' ? 1 : -1;
      items.current[(current + step + CHOICES.length) % CHOICES.length]?.focus();
    }
  }

  return (
    <span ref={anchor} className="inline-flex">
      <Button
        type="button"
        variant="outline"
        className="h-12 gap-1 px-4"
        aria-label={label}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        onClick={() => {
          focusPending.current = !open;
          setOpen(!open);
        }}
      >
        Gerar
        <ChevronDown className="size-4" aria-hidden="true" />
      </Button>
      {open ? (
        <FloatingPanel
          anchorRef={anchor}
          onDismiss={close}
          align="end"
          id={menuId}
          role="menu"
          aria-label="Faixa do EAN gerado"
          className="w-72 max-w-[calc(100vw-1rem)] rounded-2xl border border-surface-border bg-surface-card p-1.5 shadow-card"
        >
          <div onKeyDown={navigate}>
            {CHOICES.map((choice, index) => (
              <button
                key={choice.prefix}
                ref={(node) => {
                  items.current[index] = node;
                  if (node && index === 0 && focusPending.current) {
                    focusPending.current = false;
                    node.focus();
                  }
                }}
                type="button"
                role="menuitem"
                className="grid w-full gap-0.5 rounded-xl px-3 py-2 text-left hover:bg-brand-500/5 focus-visible:bg-brand-500/10 focus-visible:outline-none"
                onClick={() => choose(choice.prefix)}
              >
                <span className="text-sm font-semibold text-content">{choice.label}</span>
                <span className="text-xs leading-5 text-content-muted">{choice.detail}</span>
              </button>
            ))}
          </div>
        </FloatingPanel>
      ) : null}
    </span>
  );
}
